import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { shape, func, number } from 'prop-types'

import { Row, Col } from '@gympass/yoga'

import { Union, Left } from '@assets/icons'

import getCommercialOfferPdf from '@client/services/print'

import useInternationalization from '@hooks/useInternationalization'

import Container from '@components/Container'
import Tail from '@components/Tail'

import env from '@config/env'
import OfferCard from '../../components/OfferCard'

import * as Style from './Main.style'

function Main({ contact, actions }) {
  const intl = useIntl()
  const { country } = useInternationalization()
  const history = useHistory()
  const commercialOfferLink = `${env.gympassHost}/${country}/simulate/commercial-offer?magic-link=${contact.magicLink}`

  function handleBackButton() {
    history.goBack()
  }

  function handleDownloadFile(file) {
    const url = window.URL.createObjectURL(new Blob([file]))

    const link = document.createElement('a')

    link.href = url

    link.setAttribute('download', `${contact.company}.pdf`)

    document.body.appendChild(link)

    link.click()
  }

  async function getCommercialOfferFile() {
    const discount =
      contact.proposalValue - contact.proposalValueWithoutDiscount

    const data = {
      clientInfo: {
        company: contact.company,
        name: `${contact.firstName} ${contact.lastName}`,
        email: contact.email,
        numberOfEmployees: contact.employeesNumber,
        headquarterLocation: contact.state,
      },
      brokerInfo: {
        company: contact.brokerCompany,
        name: `${contact.brokerFirstName} ${contact.brokerLastName}`,
        email: contact.brokerEmail,
      },
      commercialOffer: {
        originalValue: contact.proposalValueWithoutDiscount,
        value: contact.proposalValue,
        discount,
        couponCode: contact.couponCode,
      },
    }

    const response = await getCommercialOfferPdf(
      data,
      country,
      'broker-proposal'
    )

    handleDownloadFile(response.data)
  }

  function getMembershipFeeLink() {
    switch (country) {
      case 'br':
        return 'https://hs.gympass.com/hubfs/Commercial%20Offer%20BR/membership_br.pdf'
      case 'uk':
        return 'https://hs.gympass.com/hubfs/Broker%20UK/UK_Employee_membership_fee_v2%20(1).pdf'
      case 'us':
      default:
        return 'https://hs.gympass.com/hubfs/membership_us.pdf'
    }
  }

  function getIntroductoryGuide() {
    switch (country) {
      case 'br':
        return 'https://hs.gympass.com/hubfs/Broker%20BR/BR_B2B_OnePager_SMB.pdf'
      case 'uk':
        return 'https://hs.gympass.com/hubfs/Broker%20UK/UK_B2B_OnePager_SMB.pdf'
      case 'us':
      default:
        return 'https://hs.gympass.com/hubfs/Broker%20US/Gympass%20Toolkit.pdf'
    }
  }

  function handleOpenMembershipFee() {
    window.open(getMembershipFeeLink(), '_blank')
  }

  function handleOpenIntroductoryGuide() {
    window.open(getIntroductoryGuide(), '_blank')
  }

  async function handleCopyLink() {
    await navigator.clipboard.writeText(commercialOfferLink)
  }

  function getBrokerName() {
    return `${contact.brokerFirstName ||
      (contact &&
        contact.brokerInfo &&
        contact.brokerInfo.name)} ${contact.brokerLastName ||
      (contact && contact.brokerInfo && contact.brokerInfo.lastName)}`
  }

  return (
    <Style.MainWrapper>
      <Container>
        <Row>
          <Col xxs={12} xl-start={4} xl={6}>
            <Style.Title>
              <FormattedMessage
                id="smb_acquisition_commercial_offer_broker.page.title"
                values={{
                  company: contact.companyName,
                }}
              />
            </Style.Title>
            <Style.Subtitle>
              <FormattedMessage
                id="smb_acquisition_commercial_offer_broker.page.subtitle"
                values={{
                  name: getBrokerName(),
                  strong: (...chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Style.Subtitle>

            <OfferCard contact={contact} actions={actions} />

            <Style.Disclaimer>
              <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.disclaimer" />
            </Style.Disclaimer>

            <Style.UsefulInfo>
              <FormattedMessage
                id="smb_acquisition_commercial_offer_broker.page.useful_info"
                values={{
                  company: contact.companyName,
                }}
              />
            </Style.UsefulInfo>
            <Style.UsefulInfoSub>
              <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.useful_info_sub" />
            </Style.UsefulInfoSub>

            <Style.Card>
              <Style.List>
                <Style.ListItem>
                  <Style.ItemDescription>
                    <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.media.proposal_download" />
                  </Style.ItemDescription>

                  <Style.LinkButton onClick={getCommercialOfferFile}>
                    <img
                      src={Union}
                      alt={
                        intl.messages[
                          'smb_acquisition_commercial_offer_broker.page.media.proposal_download'
                        ]
                      }
                    />
                    <Style.LinkText>
                      <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.download" />
                    </Style.LinkText>
                  </Style.LinkButton>
                </Style.ListItem>

                <Style.ListItem>
                  <Style.ItemDescription>
                    <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.media.plans_download" />
                  </Style.ItemDescription>

                  <Style.LinkButton onClick={handleOpenMembershipFee}>
                    <img
                      src={Union}
                      alt={
                        intl.messages[
                          'smb_acquisition_commercial_offer_broker.page.media.plans_download'
                        ]
                      }
                    />
                    <Style.LinkText>
                      <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.download" />
                    </Style.LinkText>
                  </Style.LinkButton>
                </Style.ListItem>

                <Style.ListItem>
                  <Style.ItemDescription>
                    <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.media.introduction" />
                  </Style.ItemDescription>

                  <Style.LinkButton onClick={handleOpenIntroductoryGuide}>
                    <img
                      src={Union}
                      alt={
                        intl.messages[
                          'smb_acquisition_commercial_offer_broker.page.media.introduction'
                        ]
                      }
                    />
                    <Style.LinkText>
                      <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.download" />
                    </Style.LinkText>
                  </Style.LinkButton>
                </Style.ListItem>
              </Style.List>
            </Style.Card>

            <Style.CopyLinkWrapper>
              <Style.CopyLinkDescription>
                <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.copy_link" />
              </Style.CopyLinkDescription>

              <Style.CopyLinkForm>
                <Style.InputWrapper>
                  <Style.Input id="iptLink" value={commercialOfferLink} />
                </Style.InputWrapper>

                <Style.CopyLinkButton onClick={handleCopyLink}>
                  <Style.ButtonText>
                    <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.call_to_action.copy" />
                  </Style.ButtonText>
                </Style.CopyLinkButton>
              </Style.CopyLinkForm>
            </Style.CopyLinkWrapper>

            <Style.BackLinkButton onClick={handleBackButton}>
              <img
                src={Left}
                alt={
                  intl.messages[
                    'smb_acquisition_commercial_offer_broker.page.media.introduction'
                  ]
                }
              />
              <Style.LinkText>
                <FormattedMessage id="smb_acquisition_commercial_offer_broker.page.call_to_action.return" />
              </Style.LinkText>
            </Style.BackLinkButton>
          </Col>
        </Row>
        <Tail email={contact.email} mobilePhone={contact.mobilePhone} />
      </Container>
    </Style.MainWrapper>
  )
}

Main.propTypes = {
  actions: shape({
    setFunnelStage: func,
  }).isRequired,
  contact: shape({
    id: number,
    employeesNumber: number,
  }).isRequired,
}

export default Main
